import { safeLocalStorage, safeSessionStorage } from '@shared/js/safeWebStorage';

class Storage {
	key = 'asport-web';

	data = {
		tagButtonsFilterValue: null,
		videoQuality:          0,
		viewerId:              null,
		playerRestore:         {
			eventId:  null,
			position: null,
		},
		affilId:               null,
		previewTime:           {},
		cacheTs:               null,
		loginRedirectUrl:      null,
		visitedSports:         {},
		arenaTelemetrySession: null,
	};

	constructor() {
		this.load();
	}

	load() {
		const data = safeLocalStorage.getJson(this.key);
		if (!data || typeof data !== 'object' || Array.isArray(data)) return;
		const playerRestore = data.playerRestore && typeof data.playerRestore === 'object'
			? data.playerRestore
			: {};

		this.data = {
			...this.data,
			...data,
			playerRestore: {
				...this.data.playerRestore,
				...playerRestore,
			},
		};
	}

	store() {
		// The in-memory state remains authoritative for this page when persistence is unavailable.
		safeLocalStorage.setJson(this.key, this.data);
	}

	get(key) {
		return this.data[key];
	}

	set(key, value) {
		this.data[key] = value;
		this.store();
	}

	get cacheTs() {
		return this.data.cacheTs;
	}

	setCacheTs() {
		this.set('cacheTs', Date.now());
	}

	trackVisitedSport(sportId) {
		const visitedSports = this.visitedSports;
		if (!Object.keys(visitedSports).includes(sportId.toString())) {
			visitedSports[sportId] = 1;
		} else {
			visitedSports[sportId]++;
		}

		this.data.visitedSports = visitedSports;
		this.store();
	}

	get visitedSports() {
		return this.data.visitedSports || {};
	}

	get loginRedirectUrl() {
		return this.data.loginRedirectUrl;
	}

	set loginRedirectUrl(val) {
		this.set('loginRedirectUrl', val);
	}

	get playerRestore() {
		if (!this.data) return null;
		return this.data.playerRestore;
	}

	set playerRestore(val) {
		if (!val) return;
		if (!this.data.playerRestore) this.data.playerRestore = {};

		// only store position if it's greater than 1 to avoid race condition between stored position and player position
		if (val?.position !== undefined && val?.position !== null && val?.position > 1) {
			this.data.playerRestore.position = val.position;
		}

		// Update event id if it changes
		if (val?.eventId !== undefined && val?.eventId !== null && val?.eventId !== this.data.playerRestore.eventId) {
			this.data.playerRestore = {
				eventId:  val.eventId,
				position: 0,
			};
		}
		this.store();
	}

	get tagButtonsFilterValue() {
		if (!this.data) return null;
		return this.data.tagButtonsFilterValue;
	}

	set tagButtonsFilterValue(val) {
		this.set('tagButtonsFilterValue', val);
	}

	get videoQuality() {
		if (!this.data) return null;
		return this.data.videoQuality;
	}

	set videoQuality(val) {
		this.set('videoQuality', val);
	}

	get viewerId() {
		const sessionViewerId = safeSessionStorage.getItem('viewerId');
		if (sessionViewerId) {
			return sessionViewerId;
		}

		if (!this.data) return null;

		if (!this.data.viewerId) {
			this.data.viewerId = Date.now() + Math.random().toString(36).substr(2, 9);
			this.store();
		}

		return this.data.viewerId;
	}

	get affilId() {
		return this.data.affilId;
	}

	set affilId(val) {
		// AffilId can only be set once
		if (this.data.affilId) {
			return;
		}

		const regex = /^[a-zA-Z0-9]{1,10}-[a-zA-Z0-9]{1,16}$/;
		if (!regex.test(val)) {
			throw new Error('Invalid format for affillId');
		}

		this.data.affilId = val;
		this.store();
	}

	registerPreviewTime(eventId, initial, val) {
		const existing = this.get('previewTime') || {};
		if (!(eventId in existing) || existing[eventId].lastUpdated < Date.now() - 1000 * 60 * 60 * 24) {
			existing[eventId] = {
				remaining: initial,
			};
		}

		existing[eventId].remaining = Math.max(0, existing[eventId].remaining - val);
		existing[eventId].lastUpdated = Date.now();
		this.data.previewTime = existing;
		this.store();
	}

	getPreviewTime(eventId) {
		return this.get('previewTime') && this.get('previewTime')[eventId] ? this.get('previewTime')[eventId].remaining : null;
	}

	get arenaTelemetrySession() {
		return this.data?.arenaTelemetrySession || null;
	}

	set arenaTelemetrySession(session) {
		this.set('arenaTelemetrySession', session);
	}
}

const storage = new Storage();
export default storage;
